import React, { useEffect, useState } from 'react'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { faBezierCurve, faFileContract, faLayerGroup } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ColumnFilter,
  DataTable,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  FormDialogMode,
  RowCustomAction,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Delete, Edit, Pageview } from '@material-ui/icons'
import { useTranslation } from 'react-i18next'
import {
  SettlementGroup,
  SettlementGroupPatchRequest,
  SettlementGroupPostRequest,
  SettlementGroupTable
} from '../../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import { EnumTipoProrrateo } from '../../../api/Argentia/enums/tipoProrrateo'
import { EnumTypeSettlementGroup } from '../../../api/Argentia/enums/typeSettlementGroup'
import { ContractModel } from '../../../api/Argentia/model/contracts'
import { DataTableErrorHandler } from '../../../components/errors/scopes/dataTable/dataTableError'
import { enumToSelectOptionArray } from '../../../helpers'
import { getResourceUserPermissions, hasPermission } from '../../../helpers/permissionsHelper'
import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useFormDialog,
  useHttpError
} from '../../../hooks'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { Resources } from '../../../model/RBAC/constants'
import {
  DataTableFilter,
  getCustomerDomain,
  storeDataTableFilterForCurrentPage,
  storeDataTableSearchForCurrentPage
} from '../../../redux'
import { fetchSubjects } from '../../../services/subjects.service'
import { fetchContractsBySubjectID } from '../../contracts/contracts.service'
import { mapContractsToSelectOptions } from '../../procedures/procedures.mapper'
import {
  mapSettlementGroupEditToPatchRequest,
  mapSettlementGroupToPostRequest,
  settlementGroupEditMapper,
  settlementGroupsToTableModelMapper
} from '../settlementGroups.mapper'
import { TabsSettlementGroup } from '../settlementGroups.model'
import { isSettlementGroupCodeDuplicated } from '../settlementGroups.service'

export interface SettlementGroupsTableProps {
  activeTab: TabsSettlementGroup
  userPermissions: UserPermission[]
  settlementGroups?: SettlementGroup[]
  isFetchingSettlementGroups: boolean
  onAddLoading: boolean
  onEditLoading: boolean
  onAdd: (row?: SettlementGroupPostRequest) => Promise<boolean>
  onEdit: (row: SettlementGroupPatchRequest | SettlementGroupTable) => Promise<boolean>
  onDelete?: (settlementGroupId: number) => Promise<boolean>
  onViewFlexibilities?: (group: SettlementGroupTable) => void
  onViewContracts?: (group: SettlementGroupTable, flag: EnumTypeSettlementGroup) => void
  onViewSubjects?: (group: SettlementGroupTable, flag: EnumTypeSettlementGroup) => void
  onViewDetails?: (group: SettlementGroupTable) => void
  onViewChildSettlementGroups?: (group: SettlementGroupTable) => void
  refreshSettlementGroups: () => void
  dataTableFilter?: DataTableFilter
  dataTableSearch?: string
}

export interface SettlementGroupAddDefinition {
  id: number | undefined
  code: string | undefined
  description: string | undefined
  typeSettlementGroup: EnumTypeSettlementGroup | undefined
  apportionment: EnumTipoProrrateo | undefined
  subject: SelectOption | undefined
  contracts: number[]
  startDate: Date | undefined
  endDate: Date | undefined
}

export const SettlementGroupsTable: React.FC<SettlementGroupsTableProps> = ({
  activeTab,
  userPermissions,
  settlementGroups = [],
  isFetchingSettlementGroups,
  refreshSettlementGroups,
  onAdd,
  onAddLoading,
  onEdit,
  onEditLoading,
  onDelete,
  onViewFlexibilities,
  onViewContracts,
  onViewSubjects,
  onViewDetails,
  onViewChildSettlementGroups,
  dataTableFilter,
  dataTableSearch
}) => {
  const [t] = useTranslation(['settlementGroups', 'enums'])

  const { showError, clearError, errorProps } = useHttpError()
  const {
    openForm,
    closeForm,
    setFormWaiting,
    updateFormData,
    updateFormDefinition,
    formProps,
    CustomFormDialog
  } = useFormDialog()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()
  const [isLoadingContracts, setIsLoadingContracts] = useState<boolean>(false)
  const [subjectContracts, setSubjectContracts] = useState<ContractModel[]>([])

  const settlementGroupsList = settlementGroupsToTableModelMapper(settlementGroups)
  const { info, error } = useNotify()

  const isBusinessGroupTab = activeTab === TabsSettlementGroup.BusinessGroup
  const isFlexibilityTab = activeTab === TabsSettlementGroup.groupFlexibilities
  const isGroupedBillingTab = activeTab === TabsSettlementGroup.AggregateAndSummaryInvoice
  const isVirtualBatteryTab = activeTab === TabsSettlementGroup.virtualBattery

  const getDefaultValue = (activeTab: TabsSettlementGroup) => {
    switch (activeTab) {
      case TabsSettlementGroup.BusinessGroup:
        return EnumTypeSettlementGroup.GrupoEmpresarial
      case TabsSettlementGroup.groupFlexibilities:
        return EnumTypeSettlementGroup.FlexibilidadesGrupales
      case TabsSettlementGroup.virtualBattery:
        return EnumTypeSettlementGroup.BateriaVirtual
      default:
        null
    }
  }
  const getSubjectContracts = subjectId => {
    setIsLoadingContracts(true)
    setFormWaiting(true)
    fetchContractsBySubjectID(subjectId)
      .then(response => {
        if (response.success) {
          response.data && setSubjectContracts(response.data)
        } else {
          error(t('settlementGroups:forms.add.validations.subjectContracts'))
        }
        setIsLoadingContracts(false)
      })
      .finally(() => setFormWaiting(false))
  }
  const settlementGroupAddDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        sections: [
          {
            inputs: [
              {
                name: 'description',
                label: t('settlementGroups:dataTable.columns.description'),
                type: DataType.TEXT,
                required: true,
                maxLength: 20
              },
              {
                name: 'code',
                label: t('settlementGroups:dataTable.columns.code'),
                type: DataType.TEXT,
                required: true,
                maxLength: 20
              },
              {
                name: 'typeSettlementGroup',
                label: t('settlementGroups:dataTable.columns.typeSettlementGroup'),
                required: true,
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(
                  EnumTypeSettlementGroup,
                  'enums:EnumTypeSettlementGroup'
                ),
                defaultValue: getDefaultValue(activeTab),
                readOnly: !isGroupedBillingTab
              },
              {
                name: 'apportionment',
                display: isFlexibilityTab,
                required: isFlexibilityTab,
                label: t('settlementGroups:dataTable.columns.apportionment'),
                type: DataType.SELECT,
                selectOptions: enumToSelectOptionArray(EnumTipoProrrateo, 'enums:EnumTipoProrrateo')
              },
              {
                name: 'subject',
                display: isVirtualBatteryTab,
                required: isVirtualBatteryTab,
                label: t('settlementGroups:forms.add.inputs.subject'),
                type: DataType.SEARCHFIELD,
                placeholder: t('settlementGroups:forms.add.placeholders.subject'),
                apiCall: fetchSubjects,
                onChange: (subjectSelected: any, formState: any) => {
                  //clean previous contracts from FormData
                  updateFormData({
                    ...formState,
                    contracts: []
                  })

                  //update form MULTISELECT values if field is not Null
                  subjectSelected && getSubjectContracts(subjectSelected.value)
                },
                clearable: true
              },
              {
                name: 'contracts',
                display: isVirtualBatteryTab,
                label: t('settlementGroups:forms.add.inputs.contracts'),
                type: DataType.MULTISELECT,
                isLoading: isLoadingContracts,
                selectOptions: mapContractsToSelectOptions(subjectContracts)
              },
              {
                name: 'startDate',
                display: isVirtualBatteryTab,
                label: t('settlementGroups:forms.add.inputs.startDate'),
                type: DataType.DATE
              },
              {
                name: 'endDate',
                display: isVirtualBatteryTab,
                label: t('settlementGroups:forms.add.inputs.endDate'),
                type: DataType.DATE
              }
            ]
          }
        ]
      }
    }, [
      isFlexibilityTab,
      isBusinessGroupTab,
      isVirtualBatteryTab,
      activeTab,
      isLoadingContracts,
      subjectContracts
    ])

  const virtualBatteryFormEditDefinition =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT',
        sections: [
          {
            inputs: [
              {
                name: 'description',
                label: t('settlementGroups:forms.add.inputs.description'),
                type: DataType.TEXT,
                required: true
              },
              {
                name: 'code',
                label: t('settlementGroups:forms.add.inputs.code'),
                type: DataType.TEXT,
                required: true
              }
            ]
          }
        ]
      }
    }, [])

  const handleCloseAddForm = async (newGroup: SettlementGroupAddDefinition) => {
    if (newGroup) {
      if (isSettlementGroupCodeDuplicated(newGroup.code, settlementGroups)) {
        error(t('settlementGroups:notifications.create.duplicatedCode', { code: newGroup.code }))
        return
      }
      const response = await onAdd(mapSettlementGroupToPostRequest(newGroup))
      if (response) {
        closeForm()
        refreshSettlementGroups()
      }
    } else {
      closeForm()
    }
  }

  const handleCloseEditForm = async (group: SettlementGroupAddDefinition) => {
    if (group) {
      if (isSettlementGroupCodeDuplicated(group.code, settlementGroups, group.id)) {
        error(t('settlementGroups:notifications.create.duplicatedCode', { code: group.code }))
        return
      }
      const response = await onEdit(mapSettlementGroupEditToPatchRequest(group))
      if (response) {
        closeForm()
        refreshSettlementGroups()
      }
    } else {
      closeForm()
    }
  }

  const handleOnAdd = async () => {
    if (isGroupedBillingTab) {
      await onAdd()
    } else {
      openForm({
        definition: settlementGroupAddDefinition,
        title: t('settlementGroups:dataTable.fabCustomActions.add.title'),
        mode: FormDialogMode.EDIT,
        isWaiting: false,
        onClose: handleCloseAddForm
      })
    }
  }

  const handleOnEdit = async (row: SettlementGroupTable) => {
    if (isGroupedBillingTab) {
      await onEdit(row)
    } else if (isVirtualBatteryTab) {
      openForm({
        definition: virtualBatteryFormEditDefinition,
        title: t('settlementGroups:dataTable.fabCustomActions.edit.title'),
        mode: FormDialogMode.EDIT,
        isWaiting: false,
        onClose: handleCloseEditForm,
        inputData: settlementGroupEditMapper(row)
      })
    } else {
      openForm({
        definition: settlementGroupAddDefinition,
        title: t('settlementGroups:dataTable.fabCustomActions.edit.title'),
        mode: FormDialogMode.EDIT,
        isWaiting: false,
        onClose: handleCloseEditForm,
        inputData: settlementGroupEditMapper(row)
      })
    }
  }

  const handleOnDelete = async (row: SettlementGroupTable) => {
    await getConfirmation(ConfirmationDialogs.DELETE).then(async () => {
      if (onDelete) {
        const response = await onDelete(row.id)
        response && refreshSettlementGroups()
      }
      closeConfirmation()
    })
  }

  // DataTable Config Starts

  const getRowCustomActions = (): RowCustomAction[] => {
    let rowCustomActions: RowCustomAction[] = [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('settlementGroups:dataTable.tooltips.edit'),
        onClick: (row: SettlementGroupTable) => {
          handleOnEdit(row)
        },
        userPermission: UserPermission.EDIT
      }
    ]

    const extendedClientPermissions = getResourceUserPermissions(Resources.ExtendedClients)

    if (isFlexibilityTab) {
      rowCustomActions.push(
        {
          icon: <FontAwesomeIcon size="sm" icon={faBezierCurve as IconProp} />,
          tooltip: t('settlementGroups:dataTable.tooltips.showFlexibilities'),
          onClick: (row: SettlementGroupTable) => onViewFlexibilities && onViewFlexibilities(row),
          userPermission: UserPermission.EDIT
        },
        {
          icon: <FontAwesomeIcon size="sm" icon={faFileContract as IconProp} />,
          tooltip: t('settlementGroups:dataTable.tooltips.showContracts'),
          onClick: (row: SettlementGroupTable) =>
            onViewContracts && onViewContracts(row, EnumTypeSettlementGroup.FlexibilidadesGrupales)
        },
        {
          icon: <FontAwesomeIcon size="sm" icon={faLayerGroup as IconProp} />,
          tooltip: t('settlementGroups:dataTable.tooltips.showChildSettlementGroups'),
          visibleIf: 'hasExtendedClientPermissions',
          userPermission: extendedClientPermissions?.includes(UserPermission.EDIT)
            ? UserPermission.EDIT
            : extendedClientPermissions?.includes(UserPermission.READ_ONLY)
              ? UserPermission.READ_ONLY
              : undefined,
          onClick: (row: SettlementGroupTable) =>
            onViewChildSettlementGroups && onViewChildSettlementGroups(row)
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('settlementGroups:dataTable.tooltips.delete'),
          onClick: (row: SettlementGroupTable) => {
            handleOnDelete(row)
          },
          userPermission: UserPermission.EDIT,
          visibleIf: 'flexibilitiesCanDelete'
        }
      )
    }

    if (isGroupedBillingTab) {
      rowCustomActions.push(
        {
          icon: <FontAwesomeIcon size="sm" icon={faFileContract as IconProp} />,
          tooltip: t('settlementGroups:dataTable.tooltips.showContracts'),
          onClick: (row: SettlementGroupTable) =>
            onViewContracts && onViewContracts(row, EnumTypeSettlementGroup.FacturacionAgregada)
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('settlementGroups:dataTable.tooltips.delete'),
          onClick: (row: SettlementGroupTable) => {
            handleOnDelete(row)
          },
          userPermission: UserPermission.EDIT,
          visibleIf: 'contractsCanDelete'
        }
      )
    }

    if (isBusinessGroupTab) {
      rowCustomActions.push(
        {
          icon: <FontAwesomeIcon size="sm" icon={faFileContract as IconProp} />,
          tooltip: t('settlementGroups:dataTable.tooltips.showContracts'),
          onClick: (row: SettlementGroupTable) =>
            onViewContracts && onViewContracts(row, EnumTypeSettlementGroup.GrupoEmpresarial)
        },
        {
          icon: <FontAwesomeIcon size="sm" icon={faFileContract as IconProp} />,
          tooltip: t('settlementGroups:dataTable.tooltips.showSubjects'),
          onClick: (row: SettlementGroupTable) =>
            onViewSubjects && onViewSubjects(row, EnumTypeSettlementGroup.GrupoEmpresarial)
        },
        {
          icon: <FontAwesomeIcon size="sm" icon={faLayerGroup as IconProp} />,
          tooltip: t('settlementGroups:dataTable.tooltips.showChildSettlementGroups'),
          visibleIf: 'hasExtendedClientPermissions',
          userPermission: extendedClientPermissions?.includes(UserPermission.EDIT)
            ? UserPermission.EDIT
            : extendedClientPermissions?.includes(UserPermission.READ_ONLY)
              ? UserPermission.READ_ONLY
              : undefined,
          onClick: (row: SettlementGroupTable) =>
            onViewChildSettlementGroups && onViewChildSettlementGroups(row)
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('settlementGroups:dataTable.tooltips.delete'),
          onClick: (row: SettlementGroupTable) => {
            handleOnDelete(row)
          },
          userPermission: UserPermission.EDIT,
          visibleIf: 'contractsCanDelete'
        }
      )
    }

    if (isVirtualBatteryTab) {
      rowCustomActions.push(
        {
          icon: <FontAwesomeIcon size="sm" icon={faFileContract as IconProp} />,
          tooltip: t('settlementGroups:dataTable.tooltips.showContracts'),
          onClick: (row: SettlementGroupTable) =>
            onViewContracts && onViewContracts(row, EnumTypeSettlementGroup.BateriaVirtual)
        },
        {
          userPermission: UserPermission.EDIT,
          icon: <Pageview fontSize="small" />,
          tooltip: t('settlementGroups:dataTable.tooltips.detail'),
          onClick: (row: SettlementGroupTable) => onViewDetails && onViewDetails(row)
        },
        {
          icon: <Delete fontSize="small" />,
          tooltip: t('settlementGroups:dataTable.tooltips.delete'),
          onClick: (row: SettlementGroupTable) => {
            handleOnDelete(row)
          },
          userPermission: UserPermission.EDIT,
          visibleIf: 'contractsCanDelete'
        }
      )
    }

    return rowCustomActions
  }

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: true,
    print: false,
    selectableRows: false,
    canDelete: false,
    canEdit: false,
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlementGroups:dataTable.analytics')
    },
    fabLabel: t('settlementGroups:dataTable.fab'),
    customInsertEditModal: hasPermission(Resources.Flexibility, [UserPermission.EDIT]),
    onRowInsert: async () => {
      handleOnAdd()
      return true
    },
    rowCustomActions: getRowCustomActions(),
    onFilterChange: (filter: ColumnFilter[]) => {
      storeDataTableFilterForCurrentPage(filter)
    },
    onSearchChange: (search: string) => {
      storeDataTableSearchForCurrentPage(search)
    }
  }

  const columns = [
    {
      name: 'id',
      display: false,
      type: DataType.NUMBER
    },
    {
      name: 'description',
      label: t('settlementGroups:dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'code',
      label: t('settlementGroups:dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'typeSettlementGroup',
      label: t('settlementGroups:dataTable.columns.typeSettlementGroup'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(
        EnumTypeSettlementGroup,
        'enums:EnumTypeSettlementGroup'
      )
    },
    {
      name: 'apportionment',
      label: t('settlementGroups:dataTable.columns.apportionment'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoProrrateo, 'enums:EnumTipoProrrateo'),
      display: isFlexibilityTab
    },
    {
      name: 'flexibilitiesQty',
      label: t('settlementGroups:dataTable.columns.flexibilitiesQty'),
      type: DataType.TEXT_AS_NUMBER,
      display: isFlexibilityTab
    },
    {
      name: 'contractsQty',
      label: isBusinessGroupTab || isFlexibilityTab 
        ? t('settlementGroups:dataTable.columns.relatedEntitiesQty')
        : t('settlementGroups:dataTable.columns.contractsQty'),
      type: DataType.TEXT_AS_NUMBER
    }
  ]

  useEffect(() => {
    setFormWaiting(onAddLoading)
  }, [onAddLoading])

  useEffect(() => {
    setFormWaiting(onEditLoading)
  }, [onEditLoading])

  useEffect(() => {
    updateFormDefinition(settlementGroupAddDefinition)
  }, [settlementGroupAddDefinition])

  useEffect(() => {
    updateFormDefinition(virtualBatteryFormEditDefinition)
  }, [virtualBatteryFormEditDefinition])

  // DataTable Config Ends

  return (
    <>
      {isFetchingSettlementGroups ? (
        <DataTableSkeleton />
      ) : (
        <DataTable
          data={settlementGroupsList}
          columns={columns}
          options={options}
          info={DataTableErrorHandler(errorProps)}
          userPermissions={userPermissions}
          appliedFilter={dataTableFilter}
          appliedSearch={dataTableSearch}
        />
      )}
      <CustomFormDialog {...formProps} />
    </>
  )
}
